import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/** The "How to use" walkthrough, mirroring the Code RAG / Cash Flow pattern. */
export function HowToTab() {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>What this is</CardTitle>
          <CardDescription>
            The Cinch support knowledge base (support.cinch.io) as the AI agent
            sees it. Every night a pipeline signs in, crawls every article, and
            keeps a search index in step with it — additions, edits and
            deletions included. Cinch AI answers how-to questions from that
            index. This page shows whether the index is healthy and current,
            lets you search and ask it the way the agent does, and is where the
            reviewer's daily suggestions land.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">
              Only external articles reach the agent.
            </span>{" "}
            Articles marked internal (an <em>Internal |</em> title prefix, or
            the internal categories) are dropped before indexing. If an internal
            article shows up in Search, that is a bug to report, not a feature.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>The tabs</CardTitle>
          <CardDescription>
            In the order most people reach for them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-3 pl-5 text-sm">
            <li>
              <span className="font-medium">Overview</span> — did last
              night&rsquo;s run work, and could it even sign in? A red banner
              here means the agent is answering from a stale copy. Tiles show
              how many articles and sections are indexed and which embedding
              model built them.
            </li>
            <li>
              <span className="font-medium">Articles</span> — every article the
              crawl found, with its category, when HubSpot last changed it, when
              we last crawled it, and its sync status. Click a row to read the
              article exactly as indexed.
            </li>
            <li>
              <span className="font-medium">Runs</span> — the night-by-night
              history. <em>Login failed</em> is the one to act on: the pipeline
              stops, changes nothing, and waits for a working password.
            </li>
            <li>
              <span className="font-medium">Search</span> — the agent&rsquo;s
              retrieval, by hand. Phrase it like a customer would ask
              (&ldquo;how do I stop a campaign going out at night&rdquo;). One
              result per article, best section first.
            </li>
            <li>
              <span className="font-medium">Ask</span> — a grounded answer with
              citations. Read the sources: an answer whose citations don&rsquo;t
              support it is an answer to discard, and a question the KB
              can&rsquo;t answer is a gap worth an article.
            </li>
            <li>
              <span className="font-medium">Article Review</span> — the daily
              desk. Every weekday morning the reviewer checks five articles
              against the current code and writes up what is wrong, outdated or
              missing, with a suggested rewrite. Work the queue: <em>Accept</em>{" "}
              what you will act on, <em>Done</em> once the article is updated in
              HubSpot, <em>Reject</em> what the reviewer got wrong (and say why
              — it learns from that).
            </li>
            <li>
              <span className="font-medium">Release Impact</span> — every
              Thursday the reviewer maps the week&rsquo;s code changes onto the
              articles they probably affect, and lists capabilities that have no
              article at all with a draft outline to start from.
            </li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reading the statuses</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="flex flex-col gap-3 text-sm">
            <div>
              <dt className="font-medium">Run results</dt>
              <dd className="text-muted-foreground">
                <em>OK</em> and <em>OK, no changes</em> are both good nights.{" "}
                <em>Partial</em> means some articles failed to index and will
                retry. <em>Incomplete crawl</em> means too many pages failed, so
                nothing was deleted (a flaky night must never look like a mass
                deletion). <em>Login failed</em> means the service account could
                not sign in.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Article statuses</dt>
              <dd className="text-muted-foreground">
                <em>Indexed</em> is the steady state.{" "}
                <em>Changed, re-index pending</em> and <em>Not indexed yet</em>{" "}
                clear on the next run. <em>Deleted, purge pending</em> is an
                article HubSpot no longer has whose search entries are about to
                be removed.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Review verdicts</dt>
              <dd className="text-muted-foreground">
                <em>Wrong</em> contradicts the code. <em>Outdated</em> described
                behaviour that has since changed. <em>Missing detail</em> is
                true but incomplete. <em>Fine</em> was checked and holds up — a
                positive result, not a shrug.
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
